<template>
    <div v-if="isOpen" class="nested-overlay-wrapper">
        <!-- Backdrop -->
        <div 
            class="fixed inset-0 z-50"
            @click="closeOverlay"
        ></div>
        
        <!-- Overlay Panel -->
        <div 
            ref="overlayPanel"
            class="nested-overlay-panel fixed z-51 bg-white rounded-lg shadow-2xl border border-gray-200"
            :style="overlayStyle"
        >
            <!-- Header -->
            <div class="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 rounded-t-lg z-10">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                        <h3 class="text-base font-semibold text-gray-800">
                            {{ $t('general.sub_attribute_for', { name: childName }) }}
                        </h3>
                        <span class="flex items-center justify-center min-w-[24px] h-6 px-2 text-xs text-gray-500 bg-blue-50 rounded-full border border-blue-200 font-medium">
                            {{ totalNestedCount }}
                        </span>
                    </div>
                    <button 
                        @click="closeOverlay"
                        class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                        :title="$t('general.close')"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Content -->
            <div class="overflow-y-auto p-5 space-y-2" :style="{ maxHeight: contentMaxHeight }">
                <!-- "All" option for selecting parent attribute -->
                <div 
                    class="cursor-pointer flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-200 group border border-blue-200 hover:border-blue-300 hover:shadow-sm"
                    @click="handleSelectAll"
                >
                    <input 
                        :checked="isParentSelected"
                        @click.stop="handleSelectAll"
                        type="checkbox" 
                        class="w-4 h-4 border-gray-300 rounded cursor-pointer focus:ring-primary focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                    >
                    <div class="flex items-center gap-3 flex-1">
                        <span class="text-[14px] text-blue-700 font-semibold">
                            {{ $t('search.all') }} {{ childName }}
                        </span>
                    </div>
                </div>

                <!-- Divider -->
                <div class="border-t border-gray-200 my-3"></div>

                <!-- Flat list of all nested attributes -->
                <template v-for="(nestedAttr, nestedIdx) in nestedData" :key="nestedIdx">
                    <div 
                        v-for="(nestedChild, nestedChildIdx) in nestedAttr.children" 
                        :key="`${nestedIdx}-${nestedChildIdx}`"
                        class="cursor-pointer flex items-center gap-3 p-3 hover:bg-gray-50 rounded-md transition-colors duration-200 group border border-gray-100 hover:border-blue-200 hover:shadow-sm"
                        @click="handleNestedCheck(nestedAttr.id, nestedChild.id)"
                    >
                        <input 
                            :checked="nestedChild.isChecked"
                            @click.stop="handleNestedCheck(nestedAttr.id, nestedChild.id)"
                            type="checkbox" 
                            class="w-4 h-4 border-gray-300 rounded cursor-pointer focus:ring-primary focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                        >
                        <div class="flex items-center gap-3 flex-1">
                            <span 
                                v-if="nestedChild.color"
                                class="block w-[24px] h-[24px] rounded-full border border-[#E5E5E5] shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105 group-hover:border-primary flex-shrink-0"
                                :style="{ backgroundColor: nestedChild.color }"
                            ></span>
                            <span class="text-[14px] text-gray-800 transition-all duration-200 group-hover:text-primary group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 font-medium">
                                {{ $i18n.locale === 'ar' ? nestedChild?.name_ar : nestedChild?.name_en }}
                            </span>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NestedAttributeOverlay',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
            default: false
        },
        nestedData: {
            type: Array,
            default: () => []
        },
        position: {
            type: Object,
            default: () => ({ x: 0, y: 0, maxHeight: 0 })
        },
        parentInfo: {
            type: Object,
            default: () => null
        }
    },
    emits: ['close', 'nested-check', 'select-parent'],
    data() {
        return {
            isMobile: false
        };
    },
    computed: {
        overlayStyle() {
            // On mobile, use full-width modal style
            if (this.isMobile) {
                return {
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: '500px',
                    maxHeight: '80vh'
                };
            }
            
            // On desktop, position relative to clicked element
            return {
                left: `${this.position.x}px`,
                top: `${this.position.y}px`,
                width: '380px',
                maxHeight: `${this.position.maxHeight}px`
            };
        },
        contentMaxHeight() {
            if (this.isMobile) {
                return 'calc(80vh - 80px)'; // 80vh minus header height
            }
            return `${this.position.maxHeight - 80}px`; // Subtract header height
        },
        childName() {
            if (!this.parentInfo) return '';
            return this.$i18n.locale === 'ar' ? this.parentInfo.childNameAr : this.parentInfo.childNameEn;
        },
        totalNestedCount() {
            if (!this.nestedData || !Array.isArray(this.nestedData)) return 0;
            return this.nestedData.reduce((total, attr) => {
                return total + (attr.children ? attr.children.length : 0);
            }, 0);
        },
        isParentSelected() {
            // Check if parent is checked and no nested attributes are selected
            if (!this.nestedData || !Array.isArray(this.nestedData)) return false;
            
            let hasAnyNestedSelected = false;
            this.nestedData.forEach(attr => {
                if (attr.children && Array.isArray(attr.children)) {
                    attr.children.forEach(child => {
                        if (child.isChecked) {
                            hasAnyNestedSelected = true;
                        }
                    });
                }
            });
            
            // Parent is selected if: parent checkbox is checked AND no nested attributes are selected
            return this.parentInfo?.isParentChecked && !hasAnyNestedSelected;
        }
    },
    methods: {
        closeOverlay() {
            this.$emit('close');
        },
        handleNestedCheck(nestedAttrId, nestedChildId) {
            this.$emit('nested-check', {
                parentId: this.parentInfo?.parentId,
                childId: this.parentInfo?.childId,
                nestedAttrId,
                nestedChildId
            });
        },
        handleSelectAll() {
            // Emit event to select the parent attribute (unselect all nested)
            this.$emit('select-parent', {
                parentId: this.parentInfo?.parentId,
                childId: this.parentInfo?.childId
            });
        },
        checkIfMobile() {
            if (typeof window !== 'undefined') {
                this.isMobile = window.innerWidth < 768;
            }
        }
    },
    mounted() {
        // Check if mobile
        this.checkIfMobile();
        
        // Add ESC key handler
        this.handleEscKey = (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeOverlay();
            }
        };
        document.addEventListener('keydown', this.handleEscKey);
        
        // Add resize listener to update mobile state
        this.handleResize = () => {
            this.checkIfMobile();
        };
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        // Remove ESC key handler
        if (this.handleEscKey) {
            document.removeEventListener('keydown', this.handleEscKey);
        }
        
        // Remove resize handler
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize);
        }
    }
};
</script>

<style scoped>
.nested-overlay-wrapper {
    position: fixed;
    inset: 0;
    z-index: 1000;
}

.nested-overlay-panel {
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

/* Custom scrollbar for content */
.nested-overlay-panel ::-webkit-scrollbar {
    width: 6px;
}

.nested-overlay-panel ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.nested-overlay-panel ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.nested-overlay-panel ::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

.z-51 {
    z-index: 51;
}

/* Responsive styles for mobile/tablet */
@media (max-width: 767px) {
    .nested-overlay-panel {
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        width: 90% !important;
        max-width: 500px !important;
        max-height: 80vh !important;
    }
    
    .overlay-slide-enter-from,
    .overlay-slide-leave-to {
        transform: translate(-50%, -50%) scale(0.9) !important;
    }
    
    .overlay-slide-enter-to,
    .overlay-slide-leave-from {
        transform: translate(-50%, -50%) scale(1) !important;
    }
}
</style>

